import type { AccessLevel, AuthUserDto, ModuleKey, PermissionMap, RoleKey } from '@gty/common';
import { MODULE_KEYS } from '@gty/common';

/**
 * DEVELOPMENT-ONLY FAKE SESSION.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * This exists because there is no backend yet: `packages/back` has no
 * `main.ts`, no auth module and no migration, and this machine has neither
 * Docker nor PostgreSQL — so there is nothing to log in against and no
 * database to seed a credential into. Without this, the app can only ever
 * render `/login`.
 *
 * It is NOT a security control and it is NOT a fallback. It is a stub that
 * must disappear the moment `POST /api/auth/login` exists.
 *
 * Three independent things must all be true for it to activate:
 *   1. `import.meta.env.DEV` — false in every production build.
 *   2. `VITE_DEV_AUTH === 'true'` — opt-in, absent from `.env.example`.
 *   3. The credential below must match exactly.
 *
 * Because (1) is a compile-time constant, Rollup dead-code-eliminates this
 * whole module out of `vite build` output. Verified: the string
 * 'travelyatra' does not appear in dist/.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const DEV_AUTH_ENABLED =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === 'true';

/** Matches the local owner's development credential. */
const DEV_EMAIL = 'karan.yadav@gurujitravel.in';
const DEV_PASSWORD = 'travelyatra';

/**
 * The seeded matrix, transcribed from spec §5.1.
 *
 * Deliberately NOT imported from `@gty/common/seed`: that constant is
 * restricted to `packages/back/prisma/seed.ts` by an ESLint rule and an
 * architecture test, precisely so it can never become a runtime authority on
 * the client. A dev fixture copying the values is fine — a component reaching
 * into the seed source is exactly what that rule forbids.
 *
 * F = FULL, V = VIEW, N = NONE.
 */
const MATRIX: Readonly<Record<RoleKey, Readonly<Record<ModuleKey, AccessLevel>>>> = {
  OWNER: full('FULL'),
  // WEBSITE forced to NONE here too — the one module where even Admin
  // starts without access, matching the real seed matrix exactly.
  ADMIN: { ...full('FULL'), SETTINGS_ROLES: 'NONE', WEBSITE: 'NONE' },
  SALES: {
    ...full('NONE'),
    DASHBOARD: 'FULL',
    LEAD_CRM: 'FULL',
    CUSTOMER_CRM: 'FULL',
    TOUR_MASTER: 'VIEW',
    BOOKING_MANAGEMENT: 'FULL',
    NOTIFICATIONS: 'FULL',
  },
  ACCOUNTS: {
    ...full('NONE'),
    DASHBOARD: 'FULL',
    BOOKING_MANAGEMENT: 'VIEW',
    INVOICE_GENERATOR: 'FULL',
    PAYMENTS_RECEIPTS: 'FULL',
    EXPENSE_TRACKING: 'FULL',
    NOTIFICATIONS: 'FULL',
  },
  TOUR_MANAGER: {
    ...full('NONE'),
    DASHBOARD: 'FULL',
    TOUR_MASTER: 'VIEW',
    BOOKING_MANAGEMENT: 'FULL',
    BOOKING_VOUCHER: 'FULL',
    HOTEL_VENDOR_MANAGEMENT: 'FULL',
    VEHICLE_MANAGEMENT: 'FULL',
    NOTIFICATIONS: 'FULL',
  },
  MARKETING: {
    ...full('NONE'),
    DASHBOARD: 'FULL',
    LEAD_CRM: 'FULL',
    NOTIFICATIONS: 'FULL',
  },
};

function full(level: AccessLevel): Record<ModuleKey, AccessLevel> {
  return Object.fromEntries(MODULE_KEYS.map((m) => [m, level])) as Record<ModuleKey, AccessLevel>;
}

const ROLE_NAMES: Readonly<Record<RoleKey, string>> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  SALES: 'Sales',
  ACCOUNTS: 'Accounts',
  TOUR_MANAGER: 'Tour Manager',
  MARKETING: 'Marketing',
};

export interface DevSession {
  user: AuthUserDto;
  permissions: PermissionMap;
}

/**
 * Which role the fake session gets.
 *
 * `VITE_DEV_AUTH_ROLE` lets you see the app as any of the six roles without a
 * backend — the honest version of the design's "View as role" switcher, kept
 * at build-config level where it cannot be mistaken for a runtime capability.
 */
function devRole(): RoleKey {
  const raw = import.meta.env.VITE_DEV_AUTH_ROLE;
  return raw !== undefined && raw in MATRIX ? (raw as RoleKey) : 'OWNER';
}

/**
 * Resolve a fake login. Returns null when the credential does not match, so
 * the login form still exercises its real error path.
 */
export function devLogin(email: string, password: string): DevSession | null {
  if (!DEV_AUTH_ENABLED) return null;
  if (email.trim().toLowerCase() !== DEV_EMAIL || password !== DEV_PASSWORD) return null;

  const role = devRole();

  return {
    user: {
      id: 'dev-user-0001',
      email: DEV_EMAIL,
      fullName: 'Karan Yadav',
      role: { id: `dev-role-${role}`, key: role, name: ROLE_NAMES[role] },
      tenantId: null,
    },
    permissions: { ...MATRIX[role] },
  };
}

/** Shown on the login form so the credential is never a guessing game. */
export const DEV_AUTH_HINT = { email: DEV_EMAIL, password: DEV_PASSWORD, role: devRole };
